import type { Recipient } from "@/context/CampaignBuilderContext";
import type { ManualSender } from "@/interfaces/emailPreview";

// @Signature must run first — the signature field itself may contain sender tokens
const RESOLUTION_ORDER = [
  "Signature",
  "SenderFirstName",
  "SenderLastName",
  "SenderCompany",
  "FirstName",
  "LastName",
] as const;

type TokenKey = (typeof RESOLUTION_ORDER)[number];

function applyTokenMap(template: string, map: Partial<Record<TokenKey, string>>): string {
  let result = template;
  for (const key of RESOLUTION_ORDER) {
    const value = map[key];
    if (value !== undefined) {
      result = result.replace(new RegExp(`@${key}\\b`, "g"), value);
    }
  }
  return result;
}

export function signatureToText(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}

/** The Step 2 columns a preview can fill a merge tag from. */
const PREVIEW_FIELDS = [
  "senderFirst",
  "senderLast",
  "senderCompany",
  "firstName",
  "lastName",
] as const;

type PreviewField = (typeof PREVIEW_FIELDS)[number];

/** Derived from `Recipient` so the column names and their types stay tied to the source. */
type PreviewContact = Partial<Pick<Recipient, PreviewField>>;

/**
 * The contact details a preview should display, taken from the Step 2 recipient rows.
 *
 * Each field independently takes the FIRST non-empty value across every row — not the
 * value on row 0, which may well be blank while a later row is filled in. One populated
 * row anywhere is enough for the preview to stop showing a bare tag.
 *
 * A field that no row filled in stays absent rather than empty. An absent value leaves
 * its @Token rendering literally, which is what tells the author the tag is still
 * unresolved; mapping it to "" instead would silently delete the tag from the sentence
 * and leave a hole where a name should be.
 */
export function resolvePreviewContact(recipients: readonly Recipient[]): PreviewContact {
  const contact: PreviewContact = {};
  for (const field of PREVIEW_FIELDS) {
    const match = recipients.find((r) => (r[field] ?? "").trim().length > 0);
    if (match) contact[field] = (match[field] ?? "").trim();
  }
  return contact;
}

/**
 * A manual campaign's preview "contact" — built from the campaign-level sender fields rather than a
 * recipient row, since a manual campaign has none (it hands out anonymous links). Only non-empty
 * fields are included, so an unfilled sender field leaves its @Token literal in the preview (the
 * same "still unresolved" signal `resolvePreviewContact` gives), not a silent blank. `firstName` /
 * `lastName` are intentionally NOT set: @FirstName / @LastName have no source on a manual campaign
 * and must stay literal. Mirrors the prototype's `_buildStudioTokenMap` manual branch.
 */
export function manualPreviewContact(sender: ManualSender): PreviewContact {
  const contact: PreviewContact = {};
  if (sender.firstName.trim()) contact.senderFirst = sender.firstName.trim();
  if (sender.lastName.trim()) contact.senderLast = sender.lastName.trim();
  if (sender.company.trim()) contact.senderCompany = sender.company.trim();
  return contact;
}

/** Sender tokens only — shared by the email preview and the unboxing headline. */
function senderTokenMap(contact: PreviewContact): Partial<Record<TokenKey, string>> {
  const map: Partial<Record<TokenKey, string>> = {};
  if (contact.senderFirst !== undefined) map.SenderFirstName = contact.senderFirst;
  if (contact.senderLast !== undefined) map.SenderLastName = contact.senderLast;
  if (contact.senderCompany !== undefined) map.SenderCompany = contact.senderCompany;
  return map;
}

/**
 * @Signature's text: the first Step 2 row's own "Sender Signature" that has visible text, else the
 * campaign signature (GFG-325). Judged on the stripped text, not a trim, so a cell holding only
 * markup ("<p></p>") is skipped. Either way its own sender tokens resolve from their columns.
 */
function resolveSignatureText(
  signatureRaw: string,
  recipients: readonly Recipient[],
  senderMap: Partial<Record<TokenKey, string>>,
): string {
  const recipientSignature = recipients.find(
    (r) => signatureToText(r.senderSignature ?? "").length > 0,
  )?.senderSignature;
  return applyTokenMap(signatureToText(recipientSignature ?? signatureRaw), senderMap);
}

const GFG_BRAND_NAME = "Gifts for Good";

/**
 * The sender name shown in the preview's "Sender Details" panel, which describes a real
 * inbox rather than author-editable copy — so it never leaves a raw @Token on screen.
 */
export function resolveSenderDisplayName(recipients: readonly Recipient[]): string {
  const contact = resolvePreviewContact(recipients);
  const company = contact.senderCompany ?? contact.senderFirst;
  return company ? `${company} via ${GFG_BRAND_NAME}` : GFG_BRAND_NAME;
}

/**
 * Resolves all @Token markers in a template string, against the Step 2 rows.
 *
 * @Signature resolves first, so sender tokens written inside the signature ("@SenderFirstName
 * at @SenderCompany") get resolved in the text it expands to. A row's own Sender Signature wins
 * over `signatureRaw` — see `resolveSignatureText`. A field with no data in any row is left as a
 * literal tag rather than blanked — see `resolvePreviewContact`.
 */
export function resolveAllTokens(
  template: string,
  signatureRaw: string,
  recipients: readonly Recipient[],
  manualSender?: ManualSender,
): string {
  // Manual campaigns have no recipient rows — resolve @Sender* from the campaign-level sender
  // fields instead. recipients is empty in that mode, so the signature path below still falls back
  // to signatureRaw (whose default "@SenderFirstName at @SenderCompany" then resolves from these).
  const contact = manualSender
    ? manualPreviewContact(manualSender)
    : resolvePreviewContact(recipients);
  const senderMap = senderTokenMap(contact);

  // Pre-existing behaviour, kept deliberately: with no company anywhere, the email falls back
  // to the sender's first name. The unboxing headline must NOT do this — see below.
  const company = contact.senderCompany ?? contact.senderFirst;

  const signatureText = resolveSignatureText(signatureRaw, recipients, senderMap);

  return applyTokenMap(template, {
    ...senderMap,
    ...(company !== undefined ? { SenderCompany: company } : {}),
    ...(signatureText ? { Signature: signatureText } : {}),
    ...(contact.firstName !== undefined ? { FirstName: contact.firstName } : {}),
    ...(contact.lastName !== undefined ? { LastName: contact.lastName } : {}),
  });
}

/**
 * Resolves the sender tokens in the unboxing headline, for the campaign builder's preview.
 *
 * Only the builder goes through here. The real redemption page renders a headline already
 * resolved against that gift's own sender, so nothing a recipient sees depends on this.
 *
 * Unlike the email, there is no company -> first-name fallback: the headline reads
 * "@SenderFirstName from @SenderCompany", and the fallback would render "Judd from Judd".
 */
export function resolveUnboxingHeadline(
  headline: string,
  signatureRaw: string,
  recipients: readonly Recipient[],
  manualSender?: ManualSender,
): string {
  const senderMap = senderTokenMap(
    manualSender ? manualPreviewContact(manualSender) : resolvePreviewContact(recipients),
  );
  const signatureText = resolveSignatureText(signatureRaw, recipients, senderMap);

  return applyTokenMap(headline, {
    ...senderMap,
    ...(signatureText ? { Signature: signatureText } : {}),
  });
}
